import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

import { useRouter } from "src/hooks/use-router";
import { useAuth } from "src/hooks/use-auth";
import { paths } from "src/paths";
import { SplashScreen } from "src/components/splash-screen";
import { useOnboardingState } from "src/hooks/use-onboarding-state";
import { AccountStep } from "src/sections/get-started/account-step";
import { VerifyStep } from "src/sections/get-started/verify-step";
import { PaymentStep } from "src/sections/get-started/payment-step";
import { OnboardingStep } from "src/sections/get-started/onboarding-step";
import { ResourcesDrawer } from "src/sections/get-started/resources-drawer";
import type { AuthContextType } from "src/contexts/auth/jwt-context";
import "src/pages/starter-pay.css";

const isGetSnugDomain =
  window.location.hostname.endsWith("getsnug.com") ||
  window.location.hostname === "localhost";

type Step = "account" | "verify" | "payment" | "onboarding";

function resolveStep(
  status: ReturnType<typeof useOnboardingState>["status"],
): Step {
  switch (status) {
    case "unverified":
      return "verify";
    case "unauthenticated":
    case "no_pro_group":
      return "account";
    case "unpaid":
      return "payment";
    case "paid_unboarded":
      return "onboarding";
    default:
      return "account";
  }
}

export default function GetStartedPage() {
  const router = useRouter();
  const location = useLocation();
  const { verifyEmail, signOut } = useAuth<AuthContextType>();
  const [refreshKey, setRefreshKey] = useState(0);
  const [tokenVerifying, setTokenVerifying] = useState(false);
  const [tokenError, setTokenError] = useState("");
  // Share links can auto-open the resources drawer: /get-started?resources=1
  const [resourcesOpen, setResourcesOpen] = useState(() =>
    new URLSearchParams(window.location.search).has("resources"),
  );
  const state = useOnboardingState(refreshKey);

  const token = new URLSearchParams(location.search).get("token");

  useEffect(() => {
    if (!token) return;
    setTokenVerifying(true);
    verifyEmail({ token, password: null, first_name: null })
      .then(() => {
        const clean =
          window.location.pathname +
          (window.location.search
            .replace(/[?&]token=[^&]*/, "")
            .replace(/^&/, "?") || "");
        window.history.replaceState({}, "", clean);
        setRefreshKey((k) => k + 1);
      })
      .catch(() =>
        setTokenError(
          "Could not verify your email. The link may have expired.",
        ),
      )
      .finally(() => setTokenVerifying(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The funnel only runs on getsnug.com; anywhere else, send people to sign in
  // rather than leaving them on a blank page
  useEffect(() => {
    if (!isGetSnugDomain) router.replace(paths.auth.jwt.entryStart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isGetSnugDomain) return null;

  if (tokenVerifying || state.status === "loading") return <SplashScreen />;

  if (state.status === "onboarded") {
    router.replace(paths.index);
    return <SplashScreen />;
  }

  if (state.status === "paid_unboarded" && state.selfServe) {
    router.replace(paths.dashboard.upgrade);
    return <SplashScreen />;
  }

  const step = resolveStep(state.status);

  return (
    <div className="sp-page">
      <div
        className="sp-brand"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <img
          src="https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/snug_logo.png"
          alt="Snug"
          className="sp-brand-logo"
        />
        <button
          type="button"
          className="sp-res-trigger"
          onClick={() => setResourcesOpen(true)}
        >
          <svg
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M8 3.5c-1.2-1-2.8-1.2-4.5-1v10c1.7-.2 3.3 0 4.5 1 1.2-1 2.8-1.2 4.5-1v-10c-1.7-.2-3.3 0-4.5 1Zm0 0v10" />
          </svg>
          Resources
        </button>
      </div>

      <ResourcesDrawer
        open={resourcesOpen}
        onClose={() => setResourcesOpen(false)}
      />

      <section className="sp-inner">
        <h1 className="sp-headline">
          Get started with <em>Snug.</em>
        </h1>
        <p className="sp-sub">
          Includes full platform access, white-label setup, and onboarding
          support.
        </p>

        {tokenError && <div className="sp-error">{tokenError}</div>}

        {step === "account" && <AccountStep />}
        {step === "verify" && state.status === "unverified" && (
          <VerifyStep email={state.email} />
        )}
        {step === "payment" && (
          <PaymentStep onSuccess={() => setRefreshKey((k) => k + 1)} />
        )}
        {step === "onboarding" && <OnboardingStep />}

        {step !== "account" && (
          <p
            style={{
              marginTop: "1.25rem",
              textAlign: "center",
              fontSize: "0.8rem",
              color: "#9999aa",
            }}
          >
            <button
              type="button"
              onClick={async () => {
                await signOut();
                router.replace("/auth/entry/start?ep=login");
              }}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                color: "inherit",
                cursor: "pointer",
                textDecoration: "underline",
                font: "inherit",
              }}
            >
              Sign in to a different account
            </button>
          </p>
        )}
      </section>
    </div>
  );
}
